/* The agency desk's frame: the console's own shell (apps/admin/src/shell/
 * AppShell.tsx), with the agency's modules in the rail.
 *
 * SAME LOOK, ON PURPOSE. The owner asked for the agency app to look like the
 * control centre, and the cheapest way to keep two apps looking alike is for
 * them to share the stylesheet: AppShell.module.css, ui.module.css and
 * tokens.css are COPIES of the console's. Change them there first.
 *
 * What differs from the console, and why:
 *   - the rail shows a COUNT where the console shows a data dot: every module
 *     here is live, and what an agency scans the rail for is "how many are
 *     waiting on me";
 *   - the page chip is the agency's own approval state, not a data state;
 *   - the search looks through the agency's own jobs and people, read when it
 *     opens — there is no server-side search for an agency;
 *   - the rail foot says whether requests can reach the agency at all, which
 *     is the question an empty Requests page raises;
 *   - a bell (Alerts.tsx): a chime and a desktop notice when a request
 *     arrives, and what the platform has told the agency. */

import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import type { CSSProperties, ReactNode } from 'react';
import { AGENCY_STATUS_WORD, MODULES, NAV, ROLE_WORD, type ModuleKey } from '../nav';
import { getTeam, listItems, type ItemSummary, type StaffMember } from '../data/agency';
import { signOut } from '../data/api';
import { serviceName, itemStatus, staffType } from '../data/words';
import { Icon } from '../ui';
import { Bell } from './Alerts';
import { ChangePassword } from './ChangePassword';
import { useProfile } from './profile';
import css from './AppShell.module.css';
import { ResizeHandle, usePanelWidth } from './Resizer';

/** IST, 24-hour, ticking — the same clock as the console. */
function useClock(): string {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return now.toLocaleTimeString('en-GB', { timeZone: 'Asia/Kolkata', hour12: false });
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '·';
}

const CHIP: Record<string, string> = {
  live: css.dataLive ?? '',
  partial: css.dataPartial ?? '',
  demo: css.dataDemo ?? '',
  neutral: css.dataNeutral ?? '',
};

export function AppShell({ children }: { children: ReactNode }) {
  const { profile } = useProfile();
  const location = useLocation();
  const navigate = useNavigate();
  const rail = usePanelWidth('jd.agency.railWidth', 256, 200, 420);
  const clock = useClock();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  // ---- the search: the agency's own jobs and people --------------------------
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [pool, setPool] = useState<{ items: ItemSummary[]; staff: StaffMember[] } | null>(null);
  const [poolError, setPoolError] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!searchOpen || pool) return;
    let live = true;
    Promise.all([listItems(), getTeam()])
      .then(([items, team]) => {
        if (live) setPool({ items, staff: team.staff });
      })
      .catch(() => {
        if (live) setPoolError('Your jobs and team could not be read.');
      });
    return () => {
      live = false;
    };
  }, [searchOpen, pool]);
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const t = e.target as HTMLElement | null;
      const typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
      if (e.key === '/' && !typing) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const q = query.trim().toLowerCase();
  const itemHits = useMemo(
    () =>
      q.length < 2 || !pool
        ? []
        : pool.items
            .filter((i) =>
              [i.public_id, serviceName(i.service_code), i.locality.pincode, i.locality.city_code]
                .join(' ')
                .toLowerCase()
                .includes(q),
            )
            .slice(0, 6),
    [q, pool],
  );
  const staffHits = useMemo(
    () =>
      q.length < 2 || !pool
        ? []
        : pool.staff
            .filter((s) => [s.full_name, s.phone_e164, s.staff_code, s.employee_ref ?? ''].join(' ').toLowerCase().includes(q))
            .slice(0, 6),
    [q, pool],
  );

  function go(to: string) {
    setSearchOpen(false);
    setMenuOpen(false);
    setQuery('');
    navigate(to);
  }

  const key = (location.pathname.split('/')[1] || 'overview') as ModuleKey;
  const info = MODULES[key];
  const status = AGENCY_STATUS_WORD[profile.agency.status] ?? { label: profile.agency.status, tone: 'neutral' as const };
  const counts = profile.counts;

  return (
    <div className={css.shell} style={{ '--rail-w': `${rail.width}px` } as CSSProperties}>
      {/* ------------------------------------------------------------- the rail */}
      <aside className={css.rail}>
        <div className={css.brand}>
          <div className={css.brandMark} aria-hidden="true">
            JD
          </div>
          <div className={css.brandText}>
            <span className={css.brandName}>JD Agency Desk</span>
            <span className={css.brandSub}>{profile.agency.display_name}</span>
          </div>
        </div>

        <nav className={css.nav} aria-label="Modules">
          {NAV.map((g) => (
            <div className={css.group} key={g.group}>
              <div className={css.groupLabel}>{g.group}</div>
              {g.items.map((k) => {
                const m = MODULES[k];
                const n = m.count ? (counts[m.count] ?? 0) : 0;
                return (
                  <NavLink key={k} to={`/${k}`} className={({ isActive }) => `${css.item} ${isActive ? css.itemActive : ''}`}>
                    <span className={css.itemIcon} aria-hidden="true">
                      <Icon name={m.icon} size={18} />
                    </span>
                    <span className={css.itemLabel}>{m.label}</span>
                    {n > 0 ? (
                      <span className={`${css.count} ${k === 'live' ? css.countQuiet : ''}`} aria-label={`${n} waiting`}>
                        {n}
                      </span>
                    ) : null}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className={css.connApps}>
          <div className={css.connTitle}>Your agency · {profile.agency.city_name || profile.agency.city_code}</div>
          <div className={css.connRow}>
            <span className={css.connDot} aria-hidden="true" style={profile.agency.status === 'ACTIVE' ? undefined : { background: '#fbbf24' }} />
            <span className={css.connName}>Approval</span>
            <span className={css.connState}>{status.label}</span>
          </div>
          <div className={css.connRow}>
            <span className={css.connDot} aria-hidden="true" />
            <span className={css.connName}>Requests open</span>
            <span className={css.connState}>{counts.open_offers}</span>
          </div>
          <div className={css.connRow}>
            <span className={css.connDot} aria-hidden="true" />
            <span className={css.connName}>Jobs at work</span>
            <span className={css.connState}>{counts.live_jobs}</span>
          </div>
        </div>
      </aside>
      <ResizeHandle edge="left" label="Menu width" {...rail} />

      {/* ------------------------------------------------------- header + content */}
      <div className={css.column}>
        <header className={css.header}>
          <div className={css.searchWrap}>
            <span className={css.searchIcon} aria-hidden="true">
              <Icon name="search" size={17} />
            </span>
            <input
              ref={searchRef}
              className={css.search}
              type="search"
              value={query}
              placeholder="Search your jobs and team…"
              aria-label="Search your jobs and team"
              onChange={(e) => {
                setQuery(e.target.value);
                setSearchOpen(e.target.value.trim().length > 1);
              }}
              onFocus={() => setSearchOpen(query.trim().length > 1)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setSearchOpen(false);
                  (e.target as HTMLInputElement).blur();
                }
              }}
            />
            <kbd className={css.slash} aria-hidden="true">
              /
            </kbd>
            {searchOpen ? (
              <div className={css.searchPanel}>
                {poolError ? (
                  <div className={css.searchNone}>{poolError}</div>
                ) : !pool ? (
                  <div className={css.searchNone}>Searching…</div>
                ) : (
                  <>
                    <div className={css.searchGroup}>Jobs</div>
                    {itemHits.length === 0 ? (
                      <div className={css.searchNone}>No job matches.</div>
                    ) : (
                      itemHits.map((i) => (
                        <button key={i.public_id} type="button" className={css.searchHit} onClick={() => go(`/assign/${i.public_id}`)}>
                          <span className={css.searchHitTop}>
                            <span className={css.searchRef}>{i.public_id}</span>
                            <span className={css.searchMeta}>{itemStatus(i.status)}</span>
                          </span>
                          <span className={css.searchSub}>
                            {[serviceName(i.service_code), i.locality.pincode, i.locality.city_code].filter(Boolean).join(' · ')}
                          </span>
                        </button>
                      ))
                    )}
                    <div className={css.searchGroup}>Team</div>
                    {staffHits.length === 0 ? (
                      <div className={css.searchNone}>Nobody in your team matches.</div>
                    ) : (
                      staffHits.map((s) => (
                        <button key={s.public_id} type="button" className={css.searchHit} onClick={() => go(`/team?q=${encodeURIComponent(s.full_name || s.staff_code)}`)}>
                          <span className={css.searchHitTop}>
                            <span className={css.searchRef}>{s.full_name || s.staff_code}</span>
                            <span className={css.searchMeta}>{s.confirmed ? staffType(s.staff_type) : 'asking to join'}</span>
                          </span>
                          <span className={css.searchSub}>{[s.staff_code, s.phone_e164, s.employee_ref].filter(Boolean).join(' · ')}</span>
                        </button>
                      ))
                    )}
                    <div className={css.searchNote}>Searches the jobs you won and your team, by reference, service, pincode, name, phone or employee number.</div>
                  </>
                )}
              </div>
            ) : null}
          </div>

          <div className={css.spacer} />

          <div className={css.live} title="This clock is IST">
            <span className={css.liveDot} aria-hidden="true" />
            Live · <span className={css.liveTime}>{clock}</span>
          </div>

          <Bell />

          <div className={css.who}>
            <button type="button" className={css.whoBtn} aria-expanded={menuOpen} aria-haspopup="menu" onClick={() => setMenuOpen((o) => !o)}>
              <span className={css.avatar} aria-hidden="true">
                {initials(profile.me.full_name || profile.agency.display_name)}
              </span>
              <span className={css.whoText}>
                <span className={css.whoName}>{profile.me.full_name || 'Name not given'}</span>
                <span className={css.whoRole}>
                  {ROLE_WORD[profile.me.role] ?? profile.me.role} · {profile.agency.display_name}
                </span>
              </span>
              <span className={`${css.chevron} ${menuOpen ? css.chevronUp : ''}`} aria-hidden="true">
                <Icon name="chevronDown" size={14} />
              </span>
            </button>
            {menuOpen ? (
              <>
                <button className={css.menuScrim} type="button" aria-label="Close menu" onClick={() => setMenuOpen(false)} />
                <div className={css.menu} role="menu">
                  <div className={css.menuSection}>
                    <div className={css.menuLabel}>Agency</div>
                    <div style={{ font: '700 13px var(--font-ui)', color: 'var(--ink)' }}>{profile.agency.legal_name}</div>
                    <div className={css.menuHint}>
                      {[profile.agency.contact_phone_e164, profile.agency.contact_email].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <button type="button" className={css.menuItem} onClick={() => go('/profile')}>
                    Edit profile
                  </button>
                  <button type="button" className={css.menuItem} onClick={() => go('/documents')}>
                    Documents & approval
                  </button>
                  <button
                    type="button"
                    className={css.menuItem}
                    onClick={() => {
                      setMenuOpen(false);
                      setPwOpen(true);
                    }}
                  >
                    Change password
                  </button>
                  <button type="button" className={css.menuItem} onClick={() => signOut()}>
                    Sign out
                  </button>
                </div>
              </>
            ) : null}
            <ChangePassword open={pwOpen} onClose={() => setPwOpen(false)} />
          </div>
        </header>

        <div className={css.body}>
          <main className={css.main}>
            <div className={css.mainInner}>
              {info ? (
                <div className={css.pageHead}>
                  <div className={css.grow}>
                    <h1 className={css.pageTitle}>{info.label}</h1>
                    <div className={css.pageSub}>{info.sub}</div>
                  </div>
                  <span className={`${css.dataChip} ${CHIP[status.tone]}`} title="Your agency's approval on the platform">
                    <span aria-hidden="true">{'●'}</span>
                    {status.label}
                  </span>
                </div>
              ) : null}

              {profile.agency.status === 'PENDING_REVIEW' ? (
                <div className={`${css.statusBanner} ${css.statusAmber}`}>
                  <Icon name="clock" size={16} />
                  <span>
                    The platform team is checking your agency. Until it is approved no request reaches you — meanwhile set your{' '}
                    <NavLinkText to="/coverage">services and area</NavLinkText>, upload your{' '}
                    <NavLinkText to="/documents">documents</NavLinkText> and confirm your <NavLinkText to="/team">team</NavLinkText>.
                  </span>
                </div>
              ) : profile.agency.status === 'REJECTED' ? (
                <div className={`${css.statusBanner} ${css.statusDanger}`}>
                  <Icon name="alert" size={16} />
                  <span>
                    The platform did not approve your agency
                    {profile.agency.rejection_reason ? `: ${profile.agency.rejection_reason}` : '.'} Contact the platform team to register again.
                  </span>
                </div>
              ) : profile.agency.status === 'SUSPENDED' ? (
                <div className={`${css.statusBanner} ${css.statusDanger}`}>
                  <Icon name="alert" size={16} />
                  <span>Your agency is suspended. No request reaches you until the platform team lifts it.</span>
                </div>
              ) : null}

              {children}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

function NavLinkText({ to, children }: { to: string; children: ReactNode }) {
  return (
    <NavLink to={to} style={{ color: 'inherit', textDecoration: 'underline', textUnderlineOffset: 3 }}>
      {children}
    </NavLink>
  );
}
